/**
 * A file-manager grid for one office document kind (spreadsheets or decks).
 *
 * Lists the saved documents (each a `page` with a workbook/deck content block),
 * and offers "New" (create + open a blank document) and "Open" (import a file
 * into a new persisted document). Selecting a card opens its editor route.
 */
import { type Accessor, type Component, type ComponentProps, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { FileUp, Loader2, Plus } from "@fleet/icons";
import type { Page } from "@folio/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@fleet/ui";
import { formatRelativeTime } from "@/features/notes/lib/format";

interface DocumentLibraryProps {
  testid: string;
  icon: Component<ComponentProps<"svg">>;
  heading: string;
  /** Loaded document pages (most recently edited first). */
  items: Accessor<Page[]>;
  isLoading: Accessor<boolean>;
  /** Whether the local DB / user is ready for mutations. */
  ready: Accessor<boolean>;
  busy: Accessor<false | "new" | "import">;
  newLabel: string;
  emptyTitle: string;
  emptyDescription: string;
  untitledLabel: string;
  /** File picker accept attribute (e.g. ".xlsx,.csv,.ods"). */
  accept: string;
  onNew: () => void;
  onOpenFile: (file: File) => void;
  onOpen: (pageId: string) => void;
}

export function DocumentLibrary(props: DocumentLibraryProps) {
  const { t, locale } = useI18n();
  let fileInput: HTMLInputElement | undefined;

  return (
    <div
      class="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6"
      data-testid={`${props.testid}-library`}
    >
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div class="flex flex-wrap items-center gap-2">
        <div class="mr-auto flex items-center gap-2">
          <props.icon class="h-5 w-5 text-primary" />
          <h1 class="text-lg font-semibold tracking-tight">{props.heading}</h1>
        </div>
        <input
          ref={(el) => (fileInput = el)}
          type="file"
          accept={props.accept}
          class="hidden"
          data-testid={`${props.testid}-open-input`}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            if (file) {
              props.onOpenFile(file);
            }
            e.currentTarget.value = "";
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          class="gap-1.5"
          data-testid={`${props.testid}-open`}
          disabled={props.busy() !== false || !props.ready()}
          onClick={() => fileInput?.click()}
        >
          <Show when={props.busy() === "import"} fallback={<FileUp class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Open`)}
        </Button>
        <Button
          size="sm"
          class="gap-1.5"
          data-testid={`${props.testid}-new`}
          disabled={props.busy() !== false || !props.ready()}
          onClick={() => props.onNew()}
        >
          <Show when={props.busy() === "new"} fallback={<Plus class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {props.newLabel}
        </Button>
      </div>

      {/* ── Grid ────────────────────────────────────────────────── */}
      <Show
        when={!props.isLoading()}
        fallback={
          <div class="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
            <Skeleton class="h-24 w-full rounded-xl" />
            <Skeleton class="h-24 w-full rounded-xl" />
            <Skeleton class="h-24 w-full rounded-xl" />
          </div>
        }
      >
        <Show
          when={props.items().length > 0}
          fallback={
            <div class="flex flex-1 items-center justify-center">
              <EmptyState
                icon={props.icon}
                title={props.emptyTitle}
                description={props.emptyDescription}
                action={
                  <Button
                    size="sm"
                    class="gap-1.5"
                    data-testid={`${props.testid}-new-empty`}
                    disabled={props.busy() !== false || !props.ready()}
                    onClick={() => props.onNew()}
                  >
                    <Plus class="h-4 w-4" />
                    {props.newLabel}
                  </Button>
                }
              />
            </div>
          }
        >
          <div
            class="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3"
            data-testid={`${props.testid}-grid`}
          >
            <For each={props.items()}>
              {(item) => (
                <button
                  type="button"
                  data-testid={`${props.testid}-card`}
                  class="flex flex-col gap-2 rounded-xl border border-border/50 bg-card/40 p-4 text-left transition-colors hover:border-primary/40 hover:bg-secondary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  onClick={() => props.onOpen(item.id)}
                >
                  <props.icon class="h-6 w-6 text-primary/80" />
                  <span class="truncate text-sm font-semibold text-foreground">
                    {item.title?.trim() || props.untitledLabel}
                  </span>
                  <span class="text-xs text-muted-foreground">
                    {formatRelativeTime(item.updated_at, locale())}
                  </span>
                </button>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
